import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { 
  FileText, 
  Calculator, 
  FileSpreadsheet, 
  BarChart3,
  Users,
  GitBranch,
  Target,
  Building,
  AlertCircle
} from "lucide-react";

// Import sub-components (we'll create these next)
import ContractManager from "./ContractManager";
import ContractAllocationEngine from "./ContractAllocationEngine";
import ScheduleHSummary from "./ScheduleHSummary";
import ScheduleH1Details from "./ScheduleH1Details";
import ScheduleIStatus from "./ScheduleIStatus";
import ContractFlowVisualizer from "./ContractFlowVisualizer";

const ContractAllocations = ({ calculationId, filename, trialBalance, calculation, trialBalanceAccounts }) => {
  const [activeSubTab, setActiveSubTab] = useState("contract-manager");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Contract allocation state
  const [contracts, setContracts] = useState([]);
  const [contractAllocations, setContractAllocations] = useState([]);
  const [allocationResults, setAllocationResults] = useState(null);

  // Contract callback functions
  const handleContractsUpdate = (updatedContracts) => {
    setContracts(updatedContracts);
  };

  const handleAllocationsUpdate = (updatedAllocations) => {
    setContractAllocations(updatedAllocations);
  };

  const handleAllocationResultsUpdate = (results) => {
    setAllocationResults(results);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Contract Allocations</span>
            <Badge variant="secondary">{filename}</Badge>
          </CardTitle>
          <div className="text-sm text-slate-600 mt-2">
            Contract-level cost allocation management: Direct cost mapping, indirect rate application, and Schedules H, H-1, I generation for DCAA compliance
          </div>
        </CardHeader>

        {/* Enhanced Information Panel */}
        <CardContent className="border-t bg-slate-50 dark:bg-slate-900/20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <p className="font-medium">Contract-Level Allocations & DCAA Schedules</p>
              <ul className="text-xs mt-1 space-y-1">
                <li>• Contract Management: Add/edit contracts with full metadata (ID, Name, Type, POP, Value)</li>
                <li>• Direct Cost Mapping: Tag trial balance accounts as direct costs to specific contracts</li>
                <li>• Indirect Rate Application: Distribute Fringe, OH, G&A, IR&D, B&P, COM across contracts</li>
                <li>• Schedule H: Contract summaries with total direct + indirect costs</li>
                <li>• Schedule H-1: Detailed line-item contract cost breakdowns</li>
              </ul>
            </div>
            <div>
              <p className="font-medium">DCAA Compliance & Integration</p>
              <ul className="text-xs mt-1 space-y-1">
                <li>• Schedule I: Contract status reporting (remaining funds, % complete, billing status)</li>
                <li>• Full Audit Trail: Complete allocation flow from pools to contracts</li>
                <li>• Variance Integration: Contract-level discrepancy detection and alerts</li>
                <li>• Export Ready: Automated Schedules H, H-1, I generation for ICE submission</li>
                <li>• Professional Workflow: Seamless integration with existing company-wide allocations</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 mb-6 gap-1">
          <TabsTrigger value="contract-manager" className="flex items-center space-x-1 text-xs px-2 py-2">
            <FileText className="h-3 w-3" />
            <span>Contract Manager</span>
          </TabsTrigger>
          <TabsTrigger value="contract-allocations" className="flex items-center space-x-1 text-xs px-2 py-2">
            <Calculator className="h-3 w-3" />
            <span>Contract Allocations</span>
          </TabsTrigger>
          <TabsTrigger value="schedule-h" className="flex items-center space-x-1 text-xs px-2 py-2">
            <FileText className="h-3 w-3" />
            <span>Schedule H</span>
          </TabsTrigger>
          <TabsTrigger value="schedule-h1" className="flex items-center space-x-1 text-xs px-2 py-2">
            <BarChart3 className="h-3 w-3" />
            <span>Schedule H-1</span>
          </TabsTrigger>
          <TabsTrigger value="schedule-i" className="flex items-center space-x-1 text-xs px-2 py-2">
            <Users className="h-3 w-3" />
            <span>Schedule I</span>
          </TabsTrigger>
          <TabsTrigger value="flow-visual" className="flex items-center space-x-1 text-xs px-2 py-2">
            <GitBranch className="h-3 w-3" />
            <span>Flow Visual</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contract-manager" className="space-y-6">
          <ContractManager
            calculationId={calculationId}
            trialBalanceAccounts={trialBalanceAccounts || trialBalance?.accounts || calculation?.accounts || []}
            onContractsUpdate={handleContractsUpdate}
          />
        </TabsContent>

        <TabsContent value="contract-allocations" className="space-y-6">
          <ContractAllocationEngine
            calculationId={calculationId}
            contracts={contracts}
            onAllocationsUpdate={handleAllocationsUpdate}
            onResultsUpdate={handleAllocationResultsUpdate}
          />
        </TabsContent>

        <TabsContent value="schedule-h" className="space-y-6">
          <ScheduleHSummary
            calculationId={calculationId}
            contracts={contracts}
            allocationResults={allocationResults}
          />
        </TabsContent>

        <TabsContent value="schedule-h1" className="space-y-6">
          <ScheduleH1Details
            calculationId={calculationId}
            contracts={contracts}
            allocationResults={allocationResults}
          />
        </TabsContent>

        <TabsContent value="schedule-i" className="space-y-6">
          <ScheduleIStatus
            calculationId={calculationId}
            contracts={contracts}
          />
        </TabsContent>

        <TabsContent value="flow-visual" className="space-y-6">
          <ContractFlowVisualizer
            calculationId={calculationId}
            contracts={contracts}
            contractAllocations={contractAllocations}
            allocationResults={allocationResults}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContractAllocations;